/**
 * ContentScorecard — the AI Content Discoverability™ headline numbers.
 *
 * Grouped (Joan, 2026-08-22 §2) into TECHNICAL discoverability — Accessibility
 * and Structure, both computed from checkable facts — and CONTENT
 * discoverability — Comprehensibility, a directional AI judgment — then Overall.
 * The basis of each group is stated above it and each card carries its own badge,
 * because the deterministic-vs-directional distinction is the audit's
 * credibility hook and a client should never have to infer which is which.
 *
 * The group titles and basis copy come from `CONTENT_SCORE_GROUPS` in
 * @aurora/shared, which the Markdown export reads too, so screen and document
 * describe the scores identically.
 */
import {
  CONTENT_SCORE_GROUPS,
  type ContentScorecard as Scorecard,
  type ContentScoreLine,
} from '@aurora/shared';

function band(score: number): string {
  if (score >= 75) return 'strong';
  if (score >= 55) return 'solid';
  if (score >= 35) return 'moderate';
  return 'low';
}

function Metric({
  label,
  line,
  emphasis = false,
}: {
  label: string;
  line: ContentScoreLine;
  emphasis?: boolean;
}): JSX.Element {
  const width = `${Math.max(0, Math.min(100, line.score))}%`;
  return (
    <div
      className={`score-card band-${band(line.score)}${emphasis ? ' score-card-overall' : ''}`}
    >
      <div className="score-head">
        <span className="score-label">{label}</span>
        <span className="score-value">{line.score}</span>
      </div>
      <div className="score-bar" aria-hidden>
        <div className="score-bar-fill" style={{ width }} />
      </div>
      <span
        className={`score-basis ${line.deterministic ? 'basis-fact' : 'basis-judgment'}`}
      >
        {line.deterministic ? 'Verified fact' : 'AI judgment'}
      </span>
      <p className="score-why">{line.explanation}</p>
    </div>
  );
}

function Group({
  title,
  basis,
  children,
}: {
  title: string;
  basis: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="roadmap-phase">
      <h3>{title}</h3>
      <p className="muted small">{basis}</p>
      <div className="score-grid">{children}</div>
    </div>
  );
}

export function ContentScorecard({
  scorecard,
}: {
  scorecard: Scorecard;
}): JSX.Element {
  return (
    <div>
      <Group
        title={CONTENT_SCORE_GROUPS.technical.title}
        basis={CONTENT_SCORE_GROUPS.technical.basis}
      >
        <Metric label="Accessibility" line={scorecard.accessibility} />
        <Metric label="Structure" line={scorecard.structure} />
      </Group>
      <Group
        title={CONTENT_SCORE_GROUPS.content.title}
        basis={CONTENT_SCORE_GROUPS.content.basis}
      >
        <Metric label="Comprehensibility" line={scorecard.comprehensibility} />
      </Group>
      <Group
        title={CONTENT_SCORE_GROUPS.overall.title}
        basis={CONTENT_SCORE_GROUPS.overall.basis}
      >
        <Metric
          label="Overall AI Content Discoverability"
          line={scorecard.overall}
          emphasis
        />
      </Group>
      <div className="scorecard-summary">
        <span>
          <strong>Strongest:</strong> {scorecard.strongestDimension}
        </span>
        <span>
          <strong>Weakest:</strong> {scorecard.weakestDimension}
        </span>
        <span>
          <strong>Primary blocker:</strong> {scorecard.primaryBlocker}
        </span>
      </div>
    </div>
  );
}
